/*
 * HUD — header status pill + hint + well proximity prompt + nibble overlay buttons.
 * Wires DOM events to game actions and exposes setters for the loop to update.
 */

import { $ } from '@/ui/modal';
import type { ForageState, FishState } from '@/state/types';
import { WORLD } from '@/render/palette';
import { cssScaleOf } from '@/input/pointer';

let hintHidden = false;
export function hideHint(): void {
  if (hintHidden) return;
  hintHidden = true;
  const h = document.getElementById('hint');
  if (!h) return;
  h.style.opacity = '0';
  window.setTimeout(() => h.remove(), 1100);
}

/** Auto-hide the hint after a few seconds of no input. */
export function scheduleHintAutoHide(ms = 4000): void {
  window.setTimeout(hideHint, ms);
}

/**
 * Snapshot of stargaze HUD-relevant state. Lives here as an inline shape so
 * the hud module doesn't depend on the stargaze module (one-way arrow).
 */
export interface StargazeStatusSnapshot {
  active: boolean;
  score: number;
  timeLeft: number;
}

/** Update the status pill from current minigame state. */
export function updateStatus(
  forage: ForageState,
  fish: FishState,
  stargaze: StargazeStatusSnapshot | null
): void {
  const s = $('status');
  if (forage.active) {
    s.classList.add('active');
    s.textContent = `🍄 ${forage.score}  ·  ⏱ ${Math.ceil(forage.timeLeft)}s`;
  } else if (fish.active) {
    s.classList.add('active');
    s.textContent = `🎣 ${fish.score}  ·  ⏱ ${Math.ceil(fish.timeLeft)}s`;
  } else if (stargaze && stargaze.active) {
    s.classList.add('active');
    s.textContent = `🌠 ${stargaze.score}  ·  ⏱ ${Math.ceil(stargaze.timeLeft)}s`;
  } else {
    s.classList.remove('active');
  }
}

/** Show/hide the floating "Fish here" prompt above the well based on player proximity. */
export function updateWellPrompt(
  stage: HTMLElement,
  visible: boolean
): void {
  const prompt = $('well-prompt');
  if (visible) {
    prompt.classList.add('visible');
    const { sx, sy } = cssScaleOf(stage);
    prompt.style.left = `${WORLD.wellX * sx}px`;
    prompt.style.top = `${(WORLD.wellY - 70) * sy}px`;
  } else {
    prompt.classList.remove('visible');
  }
}

/** Set nibble overlay state. */
export function setNibbleActive(active: boolean): void {
  const el = document.getElementById('nibble-tap');
  if (!el) return;
  el.classList.toggle('active', active);
}
